import Buttons from '@components/Buttons';
import ProgressIndicator from '@components/ProgressIndicator';
import Spacer from '@components/Spacer';
import isInIframe from '@helpers/isInIframe';
import React, { FC, ReactElement, RefObject, useEffect, useRef, useState } from 'react';

type Props = {
  loadFn: () => void;
  isDisabled: boolean;
  intersectTime?: number;
  showSpinner?: boolean;
};

const PaginationObserver: FC<Props> = ({ showSpinner = true, loadFn, isDisabled, intersectTime }): ReactElement => {
  const boxElement = useRef<HTMLDivElement>(null);
  const loadFnRef = useRef<Props['loadFn']>(loadFn);
  const [loadFnTimeout, setLoadFnTimeout] = useState<number | ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    loadFnRef.current = loadFn;
  }, [loadFn]);

  useEffect(() => {
    const handleIntersect = (entries: { isIntersecting: boolean }[]): void => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          if (intersectTime) {
            const timeout = setTimeout(() => loadFnRef.current(), intersectTime);
            setLoadFnTimeout(timeout);
          } else {
            loadFnRef.current();
          }
        } else {
          clearTimeout(loadFnTimeout as number);
          setLoadFnTimeout(undefined);
        }
      });
    };

    const createObserver = (boxRef: RefObject<HTMLDivElement>): void => {
      const options = {
        root: null,
        rootMargin: '0px',
        threshold: [1],
      };

      const observer = new IntersectionObserver(handleIntersect, options);
      if (boxRef.current) {
        observer.observe(boxRef.current);
      }
    };
    if (!isInIframe()) {
      createObserver(boxElement);
    }
  }, [boxElement]);

  return (
    <>
      {isInIframe() && !isDisabled && (
        <>
          <Buttons isFourth isFullWidth isExtraSmall disabled={isDisabled} isShimmering={isDisabled} onClick={() => loadFnRef?.current?.()}>
            Load more...
          </Buttons>
          <Spacer size={24} />
        </>
      )}
      {!isInIframe() && (
        <div
          ref={boxElement}
          style={{
            borderRadius: '20px',
            height: '56px',
            overflow: 'hidden',
            width: '100%',
            ...(isDisabled && showSpinner ? { bottom: '-100px', position: 'fixed' } : {}),
          }}
        >
          {isDisabled
            ? (
              <></>
            )
            : (
              <>
                {showSpinner && (
                  <>
                    <Spacer />
                    <ProgressIndicator isCentered />
                    <Spacer />
                  </>
                )}
              </>
            )}
        </div>
      )}
    </>
  );
};

export default PaginationObserver;
